import React from 'react'
import { View } from 'react-native'
import { useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import {
  EhrAppointmentAttendanceChargeKind,
  EhrAppointmentKind,
  EhrAppointmentStateKind,
} from '@src/types'
import { Text } from '@src/components/base'
import { appointmentKindSelector } from '@src/selectors/app'
import { CalendarIcon } from './CalendarIcon'
import { Card } from './Card'
import { AppointmentBody } from './AppointmentBody'
import { AppointmentPaymentMethodChip } from './AppointmentPaymentMethodChip'
import { CardProps } from './types'

interface ScheduledCallCardProps extends CardProps {
  kind: EhrAppointmentKind
  meetingAt: string
  coachName: string
  description?: string
  lastStateKind?: EhrAppointmentStateKind
  chargeKind?: EhrAppointmentAttendanceChargeKind
  badgeAccessory?: React.ReactNode
  buttonTitle?: string
  variant?: 'default' | 'chatbot'
}

export const ReservedCallCard = ({
  kind,
  meetingAt,
  coachName,
  description,
  lastStateKind,
  chargeKind,
  badgeAccessory,
  buttonTitle = 'Book your next call',
  onButtonPress,
  variant = 'default',
}: ScheduledCallCardProps) => {
  const styles = useStyleSheet(themedStyles)

  const appointmentKind = useSelector(appointmentKindSelector(kind))
  const coachingKind = appointmentKind?.coachingKind

  const serviceTitle = coachingKind?.serviceTitle || 'Coaching'
  const title = `${serviceTitle} Call`
  const duration = appointmentKind?.duration ?? '0'

  const getBadgeAccessory = () => {
    if (badgeAccessory) {
      return badgeAccessory
    }

    if (chargeKind) {
      return (
        <AppointmentPaymentMethodChip
          lastStateKind={lastStateKind}
          chargeKind={chargeKind}
          style={styles.chip}
        />
      )
    }

    return undefined
  }

  return (
    <Card
      icon={<CalendarIcon variant="date" date={meetingAt} />}
      title={title}
      description="$0 out of pocket with most insurance. You won't be charged."
      body={<AppointmentBody coachName={coachName} meetingAt={meetingAt} duration={duration} />}
      buttonTitle={buttonTitle}
      badgeAccessory={getBadgeAccessory()}
      onButtonPress={onButtonPress}
      variant={variant}
    >
      {!!description && (
        <View style={styles.descriptionContainer}>
          <Text type="small" lineSpacing="none" bold>
            ABOUT YOUR BOOKING
          </Text>
          <Text type="regular" style={styles.description}>
            {description}
          </Text>
        </View>
      )}
    </Card>
  )
}

const themedStyles = StyleService.create({
  descriptionContainer: {
    gap: 8,
  },
  description: {
    color: 'theme.text.secondary',
  },
  chip: {
    top: -3,
  },
})
